import { useCallback, useRef, useState, type ReactNode } from 'react'
import { useTheme, useToast } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { PdfExportDialog, type PdfExportOptions, type PersistedExportOptions } from './PdfExportDialog.js'
import { reportExportError, beginExportSession, markExportStage } from '../client/export-errors.js'

/**
 * useSnapshotExport — the shared "capture the live preview → PDF/image export"
 * flow, extracted verbatim from HtmlEditor so the MiniApp editor reuses the
 * exact same behavior (snapshot round-trip, stale-generation guard, PdfExport
 * dialog, download vs Save-to-Drive, persisted defaults, error breadcrumbs)
 * instead of a second copy.
 *
 * The caller supplies `getSnapshot` (usually the viewer handle's
 * `requestSnapshot`) plus naming/target context; the hook owns all the export
 * state and returns the toolbar-button state + the rendered `<PdfExportDialog>`.
 * Inputs are read through a ref so the returned callbacks stay stable and the
 * caller never has to memoize the closures it passes.
 */
export interface SnapshotExportOptions {
  /** The file being exported — routes error breadcrumbs to its debug log. */
  fileId: string
  /** Base name (no extension) for the downloaded/saved file + the doc title. */
  baseName: string
  /** Capture the current preview as script-free, assets-inlined HTML. Return
   *  null when unavailable (the hook then uses `fallbackHtml`, or errors). */
  getSnapshot: () => Promise<string | null> | undefined
  /** Raw source to fall back to when the snapshot fails (HTML editor passes the
   *  file content; the miniapp has no single-file source, so it omits this and
   *  a failed snapshot surfaces an error toast instead). */
  fallbackHtml?: string
  /** Parent folder for the fallback's relative-asset base href. */
  fallbackParentId?: string | null
  /** Default destination folder for Save-to-Drive. */
  parentId?: string | null
  /** Save-to-Drive handler (workspace host). Omitted → download-only. */
  onExportToDrive?: (blob: Blob, suggestedName: string, defaultFolderId?: string | null, title?: string) => void
  /** Live preview width at open, seeds the capture-width slider. */
  getInitialWidth?: () => number | undefined
}

export function useSnapshotExport(opts: SnapshotExportOptions): {
  capturing: boolean
  openExportDialog: () => void
  dialog: ReactNode
} {
  const toast = useToast()
  const { t: tExport } = useTranslation('collab')
  const { settings, updateSetting } = useTheme()

  // Inputs via ref so callbacks below stay stable regardless of caller churn.
  const optsRef = useRef(opts)
  optsRef.current = opts

  const [showPdfDialog, setShowPdfDialog] = useState(false)
  const [exportSnapshot, setExportSnapshot] = useState<{
    html: string
    parentId?: string | null
    initialWidth?: number
    fallback?: boolean
    preparing?: boolean
  } | null>(null)
  const [exportCapturing, setExportCapturing] = useState(false)
  // Monotonic generation — bumped on every open so a stale in-flight snapshot
  // (Cancel + reopen before it resolved) can't drop its result into state.
  const exportGenRef = useRef(0)

  const getExportFileName = useCallback((options: PdfExportOptions) => {
    const ext = options.format === 'jpeg' ? 'jpg' : options.format
    return `${optsRef.current.baseName || 'document'}.${ext}`
  }, [])

  const openExportDialog = useCallback(async () => {
    if (exportCapturing) return
    setExportCapturing(true)
    const gen = ++exportGenRef.current
    beginExportSession()
    // Open the dialog IMMEDIATELY (a "preparing" spinner) instead of freezing
    // the button while an asset-heavy page snapshots — that reads as a dead click.
    const initialWidth = optsRef.current.getInitialWidth?.()
    setExportSnapshot({ html: '', initialWidth, preparing: true })
    setShowPdfDialog(true)
    try {
      const tSnap = performance.now()
      const snapshot = await optsRef.current.getSnapshot()
      markExportStage('snapshot', performance.now() - tSnap)
      markExportStage('path', snapshot ? 'snapshot' : 'fallback')
      // Stale-generation guard (Cancel + reopen raced this await).
      if (gen !== exportGenRef.current) return
      if (snapshot) {
        setExportSnapshot({ html: snapshot, initialWidth })
      } else if (optsRef.current.fallbackHtml != null) {
        setExportSnapshot({ html: optsRef.current.fallbackHtml, parentId: optsRef.current.fallbackParentId ?? null, initialWidth, fallback: true })
      } else {
        // No snapshot and no fallback (miniapp) — nothing to export.
        setShowPdfDialog(false)
        toast.error(tExport('export.failedGeneric'))
      }
    } catch (e) {
      // Generic toast; full context goes to the file's debug log (export-errors).
      if (gen === exportGenRef.current) {
        setShowPdfDialog(false)
        reportExportError('snapshot', e, { fileId: optsRef.current.fileId })
        toast.error(tExport('export.failedGeneric'))
      }
    } finally {
      setExportCapturing(false)
    }
  }, [exportCapturing, toast, tExport])

  const handleDownloadPdf = useCallback((blob: Blob, options: PdfExportOptions) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = getExportFileName(options)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    setShowPdfDialog(false)
    setExportSnapshot(null)
  }, [getExportFileName])

  const handleSaveAsPdf = useCallback((blob: Blob, options: PdfExportOptions) => {
    const onDrive = optsRef.current.onExportToDrive
    if (!onDrive) return
    const formatLabel = (options.format ?? 'pdf').toUpperCase()
    onDrive(blob, getExportFileName(options), optsRef.current.parentId ?? null, tExport('export.saveToDriveTitle', { format: formatLabel }))
    setShowPdfDialog(false)
    setExportSnapshot(null)
  }, [getExportFileName, tExport])

  const persistExportDefaults = useCallback((o: Record<string, string | number | boolean>) => {
    void updateSetting('exportDefaults', o)
  }, [updateSetting])

  const dialog = showPdfDialog && exportSnapshot ? (
    <PdfExportDialog
      htmlContent={exportSnapshot.html}
      parentId={exportSnapshot.parentId ?? null}
      fileId={opts.fileId}
      snapshotFallback={exportSnapshot.fallback}
      preparing={exportSnapshot.preparing}
      initialCaptureWidth={exportSnapshot.initialWidth}
      documentTitle={opts.baseName}
      exportDateLabel={new Date().toLocaleDateString()}
      initialOptions={settings.exportDefaults as Partial<PersistedExportOptions> | undefined}
      onDownload={handleDownloadPdf}
      onSaveToDrive={opts.onExportToDrive ? handleSaveAsPdf : undefined}
      onPersistOptions={persistExportDefaults}
      onClose={() => {
        // Bump the gen so a still-in-flight snapshot can't reopen with stale data.
        exportGenRef.current++
        setShowPdfDialog(false)
        setExportSnapshot(null)
      }}
    />
  ) : null

  return { capturing: exportCapturing, openExportDialog, dialog }
}
